export type ColorRamp = [string, string, string, string, string, string, string, string, string, string];

export type FontWeight = 400 | 500 | 600 | 700;

const colorRamp = (...values: ColorRamp): ColorRamp => values;

const fontWeight = (value: FontWeight): FontWeight => value;

const contentRamp = colorRamp("#121929", "#121928", "#111828", "#111827", "#111827", "#111827", "#111826", "#101726", "#101725", "#101725");

/** Mantine adapter input. Components should use `tokens`, not these ramps. */
export const mantineColorRamps = {
  dark: colorRamp("#C1C2C5", "#A6A7AB", "#909296", "#5c5f66", "#373A40", "#2C2E33", "#25262b", "#1A1B1E", "#141517", "#101113"),
  green: colorRamp("#F0FDF4", "#DCFCE7", "#BBF7D0", "#86F7D0", "#4ADE80", "#22C55E", "#16A34A", "#15803D", "#166534", "#14132D"),
  blue: colorRamp("#EFF6FF", "#E9F2FF", "#CCE1FE", "#B0D2FF", "#76B0FF", "#5198F9", "#1F71E1", "#074599", "#012E6C", "#001D44"),
  indigo: colorRamp("#DDE5F4", "#CBD2E8", "#A8B5D7", "#8498C6", "#4D6FAE", "#2E5396", "#1B3D78", "#112A56", "#0A1A3A", "#051127"),
  gray: colorRamp("#F9F9FA", "#F1F3F5", "#EAECEF", "#DEE2E6", "#CFD4D9", "#AEB5BC", "#878E95", "#494056", "#353A3F", "#222529"),
  danger: colorRamp("#ffeaec", "#fdd4d6", "#f4a7ac", "#ec777e", "#e64f57", "#e3353f", "#e22732", "#c91a25", "#b31220", "#9e0419"),
  red: colorRamp("#FEF2F2", "#FEE2E2", "#FECACA", "#FCA5A5", "#F87171", "#EF4444", "#DC2626", "#B91C1C", "#991B1B", "#7F1D1D"),
  yellow: colorRamp("#FFF5EB", "#FEF3C7", "#FDE68A", "#FBD34D", "#FBBF24", "#F59E0B", "#D79706", "#B45309", "#92400E", "#78350F"),
  orange: colorRamp("#fff4e6", "#ffe8cc", "#ffd8a8", "#ffc078", "#ffa94d", "#ff922b", "#fd7e14", "#f76707", "#e8590c", "#d9480f"),
  brown: colorRamp("#FFFFFF", "#FFFFFF", "#FFFFFF", "#FFFFFF", "#FFFFFF", "#FFFFFF", "#FFFFFF", "#FFFFFF", "#FFFFFF", "#FFFFFF"),
  violet: colorRamp("#f3f0ff", "#e5dbff", "#d0bfff", "#b197fc", "#9775fa", "#845ef7", "#7950f2", "#7048e8", "#6741d9", "#5f3dc4"),
  lime: colorRamp("#f4fce3", "#e9fac8", "#d8f5a2", "#c0eb75", "#a9e34b", "#94d82d", "#82c91e", "#74b816", "#66a80f", "#5c940d"),
  teal: colorRamp("#e6fcf5", "#c3fae8", "#96f2d7", "#63e6be", "#38d9a9", "#20c997", "#12b886", "#0ca678", "#099268", "#087f5b"),
  cyan: colorRamp("#e3fafc", "#c5f6fa", "#99e9f2", "#66d9e8", "#3bc9db", "#22b8cf", "#15aabf", "#1098ad", "#0c8599", "#0b7285"),
  grape: colorRamp("#f8f0fc", "#f3d9fa", "#eebefa", "#e599f7", "#da77f2", "#cc5de8", "#be4bdb", "#ae3ec9", "#9c36b5", "#862e9c"),
  pink: colorRamp("#fff0f6", "#ffdeeb", "#fcc2d7", "#faa2c1", "#f783ac", "#f06595", "#e64980", "#d6336c", "#c2255c", "#a61e4d"),
  heading: colorRamp(...contentRamp),
  body: colorRamp(...contentRamp),
};

export const tokens = {
  breakpoint: {
    xs: "36em",
    sm: "48em",
    md: "62em",
    lg: "75em",
    xl: "88em",
  },
  color: {
    black: "#000000",
    white: "#FFFFFF",
    action: {
      primary: mantineColorRamps.indigo[6],
      informational: mantineColorRamps.blue[6],
    },
    border: {
      default: mantineColorRamps.gray[4],
      subtle: mantineColorRamps.gray[3],
      // Existing AssistantChat error-bubble border.
      danger: mantineColorRamps.danger[2],
    },
    content: {
      muted: mantineColorRamps.gray[6],
      secondary: mantineColorRamps.gray[7],
      tertiary: mantineColorRamps.gray[5],
      primary: mantineColorRamps.gray[8],
      strong: mantineColorRamps.gray[9],
      danger: mantineColorRamps.danger[5],
      dangerEmphasis: mantineColorRamps.danger[7],
      successStrong: mantineColorRamps.green[6],
      warning: mantineColorRamps.yellow[8],
      secondaryHover: "rgba(29, 26, 34, 1)",
      // Existing BlockerDisplay completed icon.
      success: mantineColorRamps.green[5],
    },
    chart: {
      series: {
        paid: "rgba(74, 222, 128, 0.3)",
        due: "rgba(230, 79, 87, 0.3)",
      },
    },
    icon: {
      automation: mantineColorRamps.indigo[4],
    },
    picker: {
      dark: mantineColorRamps.dark[4],
      indigo: mantineColorRamps.indigo[4],
      blue: mantineColorRamps.blue[4],
      red: mantineColorRamps.danger[4],
      yellow: mantineColorRamps.yellow[4],
      green: mantineColorRamps.green[4],
      gray: mantineColorRamps.gray[4],
      orange: mantineColorRamps.orange[4],
      violet: mantineColorRamps.violet[4],
      lime: mantineColorRamps.lime[4],
      teal: mantineColorRamps.teal[4],
      cyan: mantineColorRamps.cyan[4],
      grape: mantineColorRamps.grape[4],
      pink: mantineColorRamps.pink[4],
    },
    surface: {
      canvas: mantineColorRamps.gray[0],
      default: mantineColorRamps.gray[1],
      raised: mantineColorRamps.gray[2],
      highlight: mantineColorRamps.blue[2],
      // Existing TransactionForm invalid-input warning panel.
      warning: mantineColorRamps.yellow[0],
    },
  },
  radius: {
    xs: "4px",
    sm: "8px",
    md: "12px",
    lg: "16px",
    xl: "32px",
  },
  shadow: {
    xs: "0px 1px 2px rgba(16, 24, 40, 0.05)",
    sm: "0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px -2px rgba(16, 24, 40, 0.1)",
    md: "0px 4px 6px -1px rgba(16, 24, 40, 0.1), 0px 2px 4px -2px rgba(16, 24, 40, 0.1)",
    lg: "0px 10px 15px -3px rgba(16, 24, 40, 0.1), 0px 4px 6px -4px rgba(16, 24, 40, 0.1)",
    xl: "0px 20px 25px -5px rgba(16, 24, 40, 0.1), 0px 8px 10px -6px rgba(16, 24, 40, 0.1)",
  },
  spacing: {
    xs: "4px",
    sm: "8px",
    md: "12px",
    lg: "16px",
    xl: "20px",
    xxl: "24px",
    xxxl: "32px",
  },
  typography: {
    fontSize: {
      xxs: "10px",
      xs: "11px",
      sm: "12px",
      md: "14px",
      lg: "16px",
      xl: "18px",
    },
    fontWeight: {
      bold: fontWeight(700),
      semibold: fontWeight(600),
      medium: fontWeight(500),
      regular: fontWeight(400),
    },
    headings: {
      defaultWeight: fontWeight(500),
      h1: { fontSize: "48px", fontWeight: fontWeight(400), lineHeight: "60px" },
      h2: { fontSize: "34px", fontWeight: fontWeight(400), lineHeight: "44px" },
      h3: { fontSize: "24px", fontWeight: fontWeight(400), lineHeight: "32px" },
      h4: { fontSize: "20px", fontWeight: fontWeight(400), lineHeight: "28px" },
      h5: { fontSize: "18px", fontWeight: fontWeight(400), lineHeight: "24px" },
      h6: { fontSize: "16px", fontWeight: fontWeight(400), lineHeight: "20px" },
    },
    size: {
      xs: "32px",
      sm: "32px",
      md: "40px",
      lg: "48px",
      xl: "48px",
    },
    textLineHeight: {
      xxs: "12px",
      xs: "16px",
      sm: "18px",
      md: "22px",
      lg: "24px",
      xl: "28px",
    },
  },
};
